import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Truck, Plus, Trash2, Pencil, Upload, Search, XCircle, CheckCircle2, Banknote } from "lucide-react";

/**
 * Parse pasted CSV / Excel-tab text into shipping rules.
 * Header row required. Recognises columns:
 *   pin / pincode / pin_code, area / city / location, charge / rate / amount
 */
function parseBulk(text) {
  const rows = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (rows.length < 2) return [];
  const sep = rows[0].includes("\t") ? "\t" : ",";
  const cells = (l) => l.split(sep).map((c) => c.trim().replace(/^"|"$/g, ""));
  const headers = cells(rows[0]).map((h) => h.toLowerCase());
  const idx = (cands) => headers.findIndex((h) => cands.some((c) => h === c || h.startsWith(c)));
  const pinI = idx(["pin", "pincode", "pin_code"]);
  const areaI = idx(["area", "city", "location", "place"]);
  const chargeI = idx(["charge", "rate", "amount", "price"]);
  const codI = idx(["cod", "cod_allowed", "cash"]);
  if (pinI < 0 || chargeI < 0) return [];
  const truthy = (v) => {
    const s = String(v || "").trim().toLowerCase();
    if (!s) return true; // default allow
    return ["1", "y", "yes", "true", "allow", "allowed", "ok"].includes(s);
  };
  return rows.slice(1).map((line) => {
    const c = cells(line);
    return {
      pin_code: (c[pinI] || "").replace(/\D/g, ""),
      area: areaI >= 0 ? c[areaI] || "" : "",
      charge: Number(c[chargeI]) || 0,
      cod_allowed: codI >= 0 ? truthy(c[codI]) : true,
    };
  }).filter((r) => r.pin_code.length === 6);
}

export default function Shipping() {
  const [rules, setRules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [form, setForm] = useState({ pin_code: "", area: "", charge: 0, cod_allowed: true, delivery_employee_ids: [] });
  // Quick tester
  const [testPin, setTestPin] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    const [rulesRes, empsRes] = await Promise.all([
      api.get("/shipping/rules"),
      api.get("/employees").catch(() => ({ data: [] })),
    ]);
    setRules(rulesRes.data || []);
    setEmployees(empsRes.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rules;
    return rules.filter((r) => r.pin_code.includes(s) || (r.area || "").toLowerCase().includes(s));
  }, [rules, q]);

  const reset = () => setForm({ pin_code: "", area: "", charge: 0, cod_allowed: true, delivery_employee_ids: [] });

  const submitCreate = async () => {
    const pin = (form.pin_code || "").replace(/\D/g, "");
    if (pin.length !== 6) { toast.error("PIN must be 6 digits"); return; }
    try {
      await api.post("/shipping/rules", { ...form, pin_code: pin });
      toast.success("Rule saved");
      setCreating(false);
      reset();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      pin_code: r.pin_code,
      area: r.area || "",
      charge: r.charge,
      cod_allowed: r.cod_allowed !== false,
      delivery_employee_ids: r.delivery_employee_ids || [],
    });
  };

  const submitEdit = async () => {
    if (!editing) return;
    const pin = (form.pin_code || "").replace(/\D/g, "");
    if (pin.length !== 6) { toast.error("PIN must be 6 digits"); return; }
    try {
      await api.put(`/shipping/rules/${editing.id}`, { ...form, pin_code: pin });
      toast.success("Rule updated");
      setEditing(null);
      reset();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this PIN rule?")) return;
    try {
      await api.delete(`/shipping/rules/${id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Failed to delete"); }
  };

  const submitBulk = async () => {
    const parsed = parseBulk(bulkText);
    if (!parsed.length) {
      toast.error("Could not parse — need columns like `pin, area, charge` and 6-digit PINs.");
      return;
    }
    try {
      const { data } = await api.post("/shipping/rules/bulk", { rules: parsed });
      toast.success(`Saved ${data.saved} rules` + (data.skipped ? ` (${data.skipped} skipped)` : ""));
      setBulkOpen(false);
      setBulkText("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk save failed");
    }
  };

  const toggleCod = async (r) => {
    try {
      await api.put(`/shipping/rules/${r.id}`, {
        pin_code: r.pin_code,
        area: r.area || "",
        charge: r.charge,
        cod_allowed: !(r.cod_allowed !== false),
      });
      toast.success(r.cod_allowed !== false ? `COD disabled for ${r.pin_code}` : `COD enabled for ${r.pin_code}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update COD");
    }
  };

  const runTest = async () => {
    const pin = testPin.replace(/\D/g, "");
    if (pin.length !== 6) { setTestResult({ serviced: false, reason: "Enter 6 digits" }); return; }
    try {
      const { data } = await api.get(`/shipping/quote?pin=${pin}`);
      setTestResult(data);
    } catch (err) {
      setTestResult({ serviced: false, reason: err?.response?.data?.detail || "Error" });
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-shipping">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">Marketplace</div>
          <h1 className="font-heading text-3xl font-bold mt-2 flex items-center gap-2">
            <Truck className="w-7 h-7" /> Shipping rates
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Configure PIN-code-based shipping charges. Customers whose PIN is not listed will see
            &ldquo;Out of service area&rdquo; and cannot checkout.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="ship-bulk-btn">
            <Upload className="w-4 h-4 mr-1" /> Bulk import
          </Button>
          <Button
            className="bg-[#0055FF] hover:bg-[#0040CC]"
            onClick={() => { reset(); setCreating(true); }}
            data-testid="ship-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New PIN
          </Button>
        </div>
      </div>

      {/* Test lookup */}
      <Card className="border-slate-200 shadow-none mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="mono text-[11px] uppercase tracking-widest text-slate-500 md:min-w-[140px]">
            Test a PIN
          </div>
          <div className="flex-1 flex gap-2 items-center">
            <Input
              placeholder="e.g. 700001"
              value={testPin}
              onChange={(e) => setTestPin(e.target.value)}
              className="max-w-[180px]"
              data-testid="ship-test-pin"
              onKeyDown={(e) => e.key === "Enter" && runTest()}
            />
            <Button variant="outline" onClick={runTest} data-testid="ship-test-btn">
              <Search className="w-4 h-4 mr-1" /> Check
            </Button>
            {testResult && (
              <div
                className={
                  "flex items-center gap-1.5 text-sm mono ml-2 " +
                  (testResult.serviced ? "text-emerald-700" : "text-red-600")
                }
                data-testid="ship-test-result"
              >
                {testResult.serviced ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Serviced · ₹{testResult.charge?.toLocaleString("en-IN")}{" "}
                    {testResult.area ? `· ${testResult.area}` : ""}
                    <span
                      className={
                        "ml-2 px-1.5 py-0.5 rounded uppercase text-[10px] " +
                        (testResult.cod_allowed === false
                          ? "bg-slate-100 text-slate-600"
                          : "bg-emerald-50 text-emerald-700")
                      }
                    >
                      {testResult.cod_allowed === false ? "Prepaid only" : "COD ok"}
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    {testResult.reason || "Not serviced"}
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="mb-3">
        <Input
          placeholder="Search PIN or area…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md"
          data-testid="ship-search"
        />
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 mono text-xs uppercase tracking-widest">PIN</th>
                <th className="text-left px-4 py-2 mono text-xs uppercase tracking-widest">Area</th>
                <th className="text-right px-4 py-2 mono text-xs uppercase tracking-widest">Charge</th>
                <th className="text-center px-4 py-2 mono text-xs uppercase tracking-widest">COD</th>
                <th className="text-center px-4 py-2 mono text-xs uppercase tracking-widest">Emps</th>
                <th className="text-right px-4 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-400 mono text-sm">
                    {rules.length === 0
                      ? "No PIN rules yet — add one or bulk import to enable checkout."
                      : "No PIN matches your search."}
                  </td>
                </tr>
              ) : (
                filtered.map((r) => {
                  const codOn = r.cod_allowed !== false;
                  return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`ship-row-${r.pin_code}`}>
                    <td className="px-4 py-2 mono font-medium text-slate-800">{r.pin_code}</td>
                    <td className="px-4 py-2 text-slate-600">{r.area || "—"}</td>
                    <td className="px-4 py-2 text-right mono font-medium">₹{r.charge?.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => toggleCod(r)}
                        className={
                          "inline-flex items-center gap-1 mono text-[10px] px-2 py-0.5 rounded uppercase transition " +
                          (codOn
                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200")
                        }
                        title={codOn ? "Click to disable Cash on Delivery for this PIN" : "Click to enable Cash on Delivery for this PIN"}
                        data-testid={`ship-cod-toggle-${r.pin_code}`}
                      >
                        <Banknote className="w-3 h-3" />
                        {codOn ? "COD" : "Prepaid only"}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span
                        className={
                          "mono text-[10px] px-1.5 py-0.5 rounded uppercase " +
                          ((r.delivery_employee_ids || []).length > 0
                            ? "bg-blue-50 text-blue-700"
                            : "bg-slate-100 text-slate-500")
                        }
                        data-testid={`ship-emp-count-${r.pin_code}`}
                        title={(r.delivery_employee_ids || []).length > 0
                          ? `Round-robin across ${(r.delivery_employee_ids || []).length} employee(s)`
                          : "No employees assigned — orders won't auto-route"}
                      >
                        {(r.delivery_employee_ids || []).length || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => openEdit(r)}
                        className="text-slate-400 hover:text-[#0055FF] p-1"
                        title="Edit"
                        data-testid={`ship-edit-${r.pin_code}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(r.id)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Delete"
                        data-testid={`ship-delete-${r.pin_code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div className="border-t border-slate-100 px-4 py-2 mono text-[11px] text-slate-500 text-right">
              {filtered.length} rule{filtered.length === 1 ? "" : "s"}
              {q ? ` (filtered from ${rules.length})` : ""}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={creating || !!editing} onOpenChange={(v) => { if (!v) { setCreating(false); setEditing(null); reset(); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit PIN rule" : "New PIN rule"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>PIN code (6 digits)</Label>
              <Input
                value={form.pin_code}
                onChange={(e) => setForm({ ...form, pin_code: e.target.value })}
                placeholder="700001"
                maxLength={6}
                data-testid="ship-form-pin"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Area name (optional)</Label>
              <Input
                value={form.area}
                onChange={(e) => setForm({ ...form, area: e.target.value })}
                placeholder="e.g. Kolkata BBD Bagh"
                data-testid="ship-form-area"
              />
            </div>
            <div className="space-y-2">
              <Label>Shipping charge (₹)</Label>
              <Input
                type="number"
                value={form.charge}
                onChange={(e) => setForm({ ...form, charge: e.target.value })}
                data-testid="ship-form-charge"
              />
            </div>
            <label className="flex items-center gap-2 border border-slate-200 rounded-md p-3 cursor-pointer hover:bg-slate-50 select-none">
              <input
                type="checkbox"
                checked={!!form.cod_allowed}
                onChange={(e) => setForm({ ...form, cod_allowed: e.target.checked })}
                data-testid="ship-form-cod"
              />
              <div className="flex-1">
                <div className="text-sm font-medium flex items-center gap-1">
                  <Banknote className="w-3.5 h-3.5 text-emerald-600" /> Allow Cash on Delivery
                </div>
                <div className="mono text-[10px] text-slate-500 mt-0.5">
                  Untick for risky / remote areas — customers there will see only &ldquo;Pay online&rdquo;.
                </div>
              </div>
            </label>

            <div className="space-y-2">
              <Label>Delivery employees (round-robin)</Label>
              <div className="border border-slate-200 rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                {employees.length === 0 ? (
                  <div className="text-xs text-slate-400 mono py-2 text-center">
                    No employees yet — add one from the Employees page first.
                  </div>
                ) : (
                  employees.map((e) => {
                    const checked = (form.delivery_employee_ids || []).includes(e.id);
                    return (
                      <label
                        key={e.id}
                        className="flex items-center gap-2 hover:bg-slate-50 rounded p-1.5 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(ev) => {
                            const cur = form.delivery_employee_ids || [];
                            setForm({
                              ...form,
                              delivery_employee_ids: ev.target.checked
                                ? [...cur, e.id]
                                : cur.filter((id) => id !== e.id),
                            });
                          }}
                          data-testid={`ship-form-emp-${e.id}`}
                        />
                        <div className="flex-1">
                          <div className="text-sm">{e.name || e.email}</div>
                          <div className="mono text-[10px] text-slate-500">{e.email}</div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              <p className="text-[10px] text-slate-500">
                Orders confirmed for this PIN will be auto-assigned to these employees in rotation.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); reset(); }}>Cancel</Button>
            <Button onClick={editing ? submitEdit : submitCreate} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="ship-form-submit">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bulk import PIN rules</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">
            Paste from Excel / CSV. First row must be headers. Recognised columns:
            <span className="mono ml-1">pin, area, charge, cod</span>. Up to 5000 rows.
            <span className="block mt-1 text-slate-400">
              &ldquo;cod&rdquo; column is optional — use <span className="mono">yes/no</span> to allow or block Cash on Delivery per PIN (defaults to yes).
            </span>
          </div>
          <Textarea
            rows={12}
            className="mono text-xs"
            placeholder={`pin\tarea\tcharge\tcod\n700001\tKolkata BBD Bagh\t60\tyes\n700016\tPark Street\t80\tyes\n560001\tBangalore MG Road\t120\tno`}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            data-testid="ship-bulk-textarea"
          />
          <div className="mono text-[11px] text-slate-500">
            {(() => { const p = parseBulk(bulkText); return p.length ? `${p.length} valid PIN rows parsed` : bulkText ? "0 valid rows — check headers and PIN format" : ""; })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={submitBulk} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="ship-bulk-submit">
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
